import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, currency } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search, UserPlus, Phone, Users as UsersIcon } from "lucide-react";
import RegisterCustomerDialog from "@/components/RegisterCustomerDialog";

const STATUS_TONE = {
  active: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100",
  inactive: "bg-slate-200 text-slate-700 hover:bg-slate-200",
  blocked: "bg-red-100 text-red-800 hover:bg-red-100",
};

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [openRegister, setOpenRegister] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = q ? { q } : {};
      const r = await api.get("/customers", { params });
      setRows(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load customers");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { const t = setTimeout(load, 220); return () => clearTimeout(t); }, [q]);

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="customers-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl" data-testid="customers-title">Members</h1>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">Customer profiles & loyalty</p>
        </div>
        <Button onClick={() => setOpenRegister(true)} className="rounded-full bg-primary hover:bg-primary/90" data-testid="register-customer-btn">
          <UserPlus className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> New customer
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
        <Input
          className="pl-9"
          placeholder="Search by name or cellphone number…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="customers-search"
        />
      </div>

      <div className="bg-white rounded-lg border">
        {busy && rows.length === 0 ? (
          <div className="p-8 text-sm text-muted-foreground text-center">Loading customers…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground text-sm flex flex-col items-center gap-2" data-testid="customers-empty">
            <UsersIcon className="w-6 h-6" strokeWidth={1.5} />
            No customers found. Use "New customer" to register the first member.
          </div>
        ) : (
          <div className="divide-y">
            {rows.map((c) => (
              <Link
                key={c.id}
                to={`/customers/${c.id}`}
                className="flex items-center justify-between p-4 hover:bg-muted/40 transition-colors duration-150"
                data-testid={`customer-row-${c.id}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{c.first_name} {c.surname}</div>
                  <div className="text-[11px] font-mono text-muted-foreground flex items-center gap-1">
                    <Phone className="w-3 h-3" strokeWidth={1.5} />
                    {c.phone_normalised || c.phone_original}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="font-mono text-sm tabular-nums font-semibold" data-testid={`customer-balance-${c.id}`}>
                      {c.loyalty_balance} pts
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Balance</div>
                  </div>
                  <Badge className={`text-[10px] ${STATUS_TONE[c.status] || STATUS_TONE.active}`}>
                    {String(c.status || "active").toUpperCase()}
                  </Badge>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <RegisterCustomerDialog
        open={openRegister}
        onOpenChange={setOpenRegister}
        onCreated={() => { setOpenRegister(false); load(); }}
      />
    </div>
  );
}
